/**
 * Modal pour ajuster finement le statut d'un étudiant :
 *  - changer le statut (présent, retard, absent, justifié, incident)
 *  - retard : nombre de minutes
 *  - justifié : motif libre
 *  - commentaire libre
 */
import { useState } from 'react';
import {
  X, CheckCircle2, Clock, XCircle, FileCheck, AlertTriangle,
} from 'lucide-react';
import type { AppelEtudiantItem } from '../../services/vieScolaireApi';

type Statut = AppelEtudiantItem['statut'];

interface PresenceLocale {
  matricule: string;
  classe: string;
  statut: Statut;
  minutes_retard: number;
  motif: string;
  commentaire: string;
}

const CHOIX: { v: Statut; label: string; couleur: string; Icon: any }[] = [
  { v: 'present',  label: 'Présent',     couleur: 'green',  Icon: CheckCircle2 },
  { v: 'retard',   label: 'Retard',      couleur: 'amber',  Icon: Clock },
  { v: 'absent',   label: 'Absent',      couleur: 'red',    Icon: XCircle },
  { v: 'justifie', label: 'Justifiée',   couleur: 'blue',   Icon: FileCheck },
  { v: 'incident', label: 'Incident',    couleur: 'orange', Icon: AlertTriangle },
];

const COULEUR_BG_ACTIF: Record<string, string> = {
  green:  'bg-green-600 text-white border-green-600',
  amber:  'bg-amber-500 text-white border-amber-500',
  red:    'bg-red-600 text-white border-red-600',
  blue:   'bg-blue-600 text-white border-blue-600',
  orange: 'bg-orange-500 text-white border-orange-500',
};

export default function ModalDetailPresence({
  etudiant, marque, onClose, onValider,
}: {
  etudiant: AppelEtudiantItem;
  marque?: PresenceLocale;
  onClose: () => void;
  onValider: (patch: Partial<PresenceLocale>) => void;
}) {
  const [statut, setStatut] = useState<Statut>(marque?.statut || etudiant.statut || 'present');
  const [minutesRetard, setMinutesRetard] = useState<number>(marque?.minutes_retard ?? etudiant.minutes_retard ?? 0);
  const [motif, setMotif] = useState<string>(marque?.motif ?? etudiant.motif ?? '');
  const [commentaire, setCommentaire] = useState<string>(marque?.commentaire ?? etudiant.commentaire ?? '');

  const handleValider = () => {
    onValider({
      statut,
      minutes_retard: statut === 'retard' ? minutesRetard : 0,
      motif: statut === 'justifie' ? motif : '',
      commentaire,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold text-base text-gray-900 dark:text-white truncate">
              {etudiant.nom} {etudiant.prenoms}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-0.5">
              <span>{etudiant.matricule}</span>
              {etudiant.classe && (
                <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px] font-medium">
                  {etudiant.classe}
                </span>
              )}
            </div>
            {etudiant.justification_par_parent && !etudiant.justification_validee && (
              <div className="mt-1.5 text-[10px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded inline-block">
                Justification déclarée par le parent — à valider
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </div>

        {/* Corps */}
        <div className="p-4 space-y-4">
          {/* Statut */}
          <div>
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Statut</div>
            <div className="grid grid-cols-2 gap-1.5">
              {CHOIX.map(c => {
                const actif = statut === c.v;
                const baseCl = actif
                  ? COULEUR_BG_ACTIF[c.couleur]
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
                return (
                  <button key={c.v} onClick={() => setStatut(c.v)}
                    className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium rounded-lg border ${baseCl}`}>
                    <c.Icon size={13} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Retard */}
          {statut === 'retard' && (
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1.5">
                Minutes de retard
              </label>
              <div className="flex items-center gap-2">
                <input type="number" value={minutesRetard} min={1} max={120}
                  onChange={(e) => setMinutesRetard(parseInt(e.target.value) || 0)}
                  className="w-24 px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100" />
                <div className="flex gap-1">
                  {[5, 10, 15, 30].map(m => (
                    <button key={m} onClick={() => setMinutesRetard(m)}
                      className="px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                      {m}'
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Motif justification */}
          {statut === 'justifie' && (
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1.5">
                Motif de la justification
              </label>
              <textarea value={motif} onChange={(e) => setMotif(e.target.value)}
                rows={2} maxLength={1000}
                placeholder="Ex. : Certificat médical, autorisation parentale…"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100" />
            </div>
          )}

          {/* Commentaire */}
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-1.5">
              Commentaire (optionnel)
            </label>
            <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
              rows={2} maxLength={300}
              className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100" />
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Annuler
          </button>
          <button onClick={handleValider}
            className="px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
